import React from 'react';

import { CheckOutlined, DashboardOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { ValidationRuleTemplateCategory, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { ValidationRuleCategories } from '@recurrency/core-api-schema/dist/validationRules/common';
import { ValidationRuleTemplateDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRuleTemplates';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Button } from 'components/Button';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { PageHeader } from 'components/PageHeader';
import { BadgeStatus, labelByStatus, StatusBadge } from 'components/recipes/StatusBadge';
import { ResultCount } from 'components/ResultCount';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { useHashState } from 'utils/routes';
import { asKeyOf } from 'utils/tables';
import { track, TrackEvent } from 'utils/track';

import { ValidationRulesTemplatesPageHashState } from 'types/hash-state';

import { EditRuleOrTemplateUpdate, UpsertRuleOrTemplateModal } from '../modals/UpsertRuleOrTemplateModal';
import { TemplateIcon } from '../TemplateIcon';

const booleanFilterOptions = [
  { label: 'All', value: '' },
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

export const ValidationRulesTemplatesPage = () => {
  const [hashState, updateHashState] = useHashState<ValidationRulesTemplatesPageHashState>();
  const tableProps = useCoreApiTableProps({
    schema: schemas.validationRules.getRuleTemplates,
    queryParams: {
      filter: {
        showInactive: true, // Show both active and inactive templates
        isDailyWorkflow: hashState.isDailyWorkflow ?? undefined,
        showInternal: hashState.internalOnly ?? undefined,
      },
    },
    mapItemFn: (item: ValidationRuleTemplateDTO) => ({
      ...item,
      key: item.templateId,
    }),
  });

  const { reload } = tableProps;

  const columns = [
    {
      title: 'Icon',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('icon'),
      align: 'center' as const,
      key: 'icon',
      render: (value: ValidationRuleCategories) => (value ? <TemplateIcon icon={value} /> : ''),
    },
    {
      title: 'Name',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('name'),
      key: 'ruleName',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('isActive'),
      key: 'isActive',
      render: (isActive: boolean) => (
        <StatusBadge
          status={isActive ? BadgeStatus.Active : BadgeStatus.Inactive}
          label={labelByStatus[isActive ? 'ACTIVE' : 'INACTIVE']}
        />
      ),
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('description'),
      key: 'lastRunTime',
      render: (description: string) => (
        <div className={css({ maxWidth: '600px', textOverflow: 'ellipsis' })}>{description}</div>
      ),
      sorter: true,
    },
    {
      title: 'Daily Workflow',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('isDailyWorkflow'),
      key: 'isDailyWorkflow',
      render: (isDailyWorkflow: boolean) => (isDailyWorkflow ? 'Yes' : 'No'),
    },
    {
      title: 'Internal Only',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('internalOnly'),
      align: 'center' as const,
      key: 'internalOnly',
      render: (value: boolean) => (value ? <CheckOutlined /> : null),
    },
    {
      title: 'Is Popular',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('isMostPopular'),
      align: 'center' as const,
      key: 'isMostPopular',
      render: (value: boolean) => (value ? <CheckOutlined /> : null),
    },
    {
      title: 'Category',
      dataIndex: asKeyOf<ValidationRuleTemplateDTO>('category'),
      align: 'center' as const,
      key: 'category',
      render: (value: ValidationRuleTemplateCategory) => value || '',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: ValidationRuleTemplateDTO) => {
        const activationText = record.isActive ? 'Deactivate' : 'Activate';
        return (
          <FlexSpace direction="row">
            <ActionButton
              onClick={async () => {
                await showAsyncModal(UpsertRuleOrTemplateModal, {
                  onSubmit: async (updates: EditRuleOrTemplateUpdate) => {
                    await coreApiFetch(schemas.validationRules.patchRuleTemplate, {
                      pathParams: {
                        templateId: record.templateId,
                      },
                      bodyParams: {
                        ...updates,
                        icon: updates.ruleCategory, // Template's icon stores the rule's default category
                      },
                    });
                    reload();
                  },
                  rule: {
                    name: record.name,
                    description: record.description,
                    sql: record.sql,
                    isDailyWorkflow: record.isDailyWorkflow,
                    internalOnly: record.internalOnly,
                    category: record.category,
                    ruleCategory: record.icon,
                  },
                  template: true,
                });
              }}
              label="Edit"
            />
            <span>|</span>
            <ActionButton
              onClick={async () => {
                const didConfirm = await showAsyncModal(ConfirmModal, {
                  title: `${activationText} Template`,
                  content: `Are you sure you want to ${activationText.toLowerCase()} this template?`,
                  okText: activationText,
                  permissionsRequired: [TenantUserPermission.ActionCreateRules],
                });
                if (didConfirm) {
                  await coreApiFetch(schemas.validationRules.patchRuleTemplate, {
                    pathParams: {
                      templateId: record.templateId,
                    },
                    bodyParams: {
                      isActive: !record.isActive,
                    },
                  });
                  reload();
                }
              }}
              label={activationText}
            />
          </FlexSpace>
        );
      },
    },
  ].filter(truthy);

  return (
    <Container>
      <PageHeader
        title={
          <InfoTooltip placement="bottom" title="Templates to create validation rules from.">
            Validation Rule Templates
          </InfoTooltip>
        }
        headerActions={[
          <Button
            key="new-rule-from-template-button"
            type="primary"
            onClick={async () => {
              // open modal
              await showAsyncModal(UpsertRuleOrTemplateModal, {
                onSubmit: async (updates: EditRuleOrTemplateUpdate) => {
                  const result = await coreApiFetch(schemas.validationRules.createRuleTemplate, {
                    bodyParams: {
                      name: updates.name,
                      description: updates.description,
                      sql: updates.sql,
                      isDailyWorkflow: updates.isDailyWorkflow,
                      internalOnly: updates.internalOnly,
                      icon: updates.ruleCategory,
                      category: updates.category,
                    },
                  });
                  if (result?.status === 200) {
                    track(TrackEvent.ValidationRules_CreateTemplate, {
                      templateId: result.data.templateId,
                      name: updates.name,
                      description: updates.description,
                      isDailyWorkflow: updates.isDailyWorkflow ?? false,
                      internalOnly: updates.internalOnly ?? false,
                      icon: updates.ruleCategory ?? '',
                      category: updates.category ?? '',
                    });
                  }
                  reload();
                },
                template: true,
              });
            }}
          >
            <PlusOutlined /> New Template
          </Button>,
        ]}
      />
      <FilterBarBox dividerLine>
        <FlexSpace direction="column" gap={16} fullWidth>
          <FlexSpace wrap fullWidth>
            <span>Filter</span>
            <AsyncMultiSelect
              label="Is Daily Workflow"
              mode="single"
              icon={<DashboardOutlined />}
              selectProps={{
                options: booleanFilterOptions.map((option, index) => ({
                  ...option,
                  key: index,
                })),
              }}
              selectedValues={[
                hashState.isDailyWorkflow === undefined ? 'All' : hashState.isDailyWorkflow ? 'Yes' : 'No',
              ]}
              onSelectedValuesChange={(values) =>
                updateHashState({
                  isDailyWorkflow: values[0] === 'Yes' ? true : values[0] === 'No' ? false : undefined,
                })
              }
            />

            <AsyncMultiSelect
              label="Internal Only"
              mode="single"
              icon={<LockOutlined />}
              selectProps={{
                options: booleanFilterOptions.map((option, index) => ({
                  ...option,
                  key: index,
                })),
              }}
              selectedValues={[hashState.internalOnly === undefined ? 'All' : hashState.internalOnly ? 'Yes' : 'No']}
              onSelectedValuesChange={(values) =>
                updateHashState({
                  internalOnly: values[0] === 'Yes' ? true : values[0] === 'No' ? false : undefined,
                })
              }
            />
            <FlexSpacer />
            <ResultCount count={tableProps.totalCount} />
          </FlexSpace>
        </FlexSpace>
      </FilterBarBox>

      <AsyncTable tableProps={tableProps} columns={columns} />
    </Container>
  );
};
